import { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { emit } from "@/lib/event-bus";

/** { / } for prev/next tab — raw keydown because react-hotkeys-hook can't match shifted chars. */
function useTabPrevNext() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "{") {
        e.preventDefault();
        emit("hotkey:tab-prev");
      } else if (e.key === "}") {
        e.preventDefault();
        emit("hotkey:tab-next");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
}

/**
 * Global keyboard shortcuts — wired at the AppShell level.
 * Each shortcut emits a typed event on the bus so any component
 * can react via useEventListener without prop drilling.
 */
export function useAppHotkeys() {
  // Save template
  useHotkeys("mod+s", () => emit("hotkey:save"), { preventDefault: true });

  // Revert changes
  useHotkeys("mod+z", () => emit("hotkey:revert"), { preventDefault: true });

  // Tab switching ({ / } and Cmd+1–9, Cmd+0 for 10th)
  // Shift+[/] produce "{"/"}" as key — react-hotkeys-hook can't match shifted chars,
  // so these are bound via raw keydown in useEffect below.
  useTabPrevNext();
  useHotkeys("mod+1", () => emit("hotkey:tab-index", 0), {
    preventDefault: true,
  });
  useHotkeys("mod+2", () => emit("hotkey:tab-index", 1), {
    preventDefault: true,
  });
  useHotkeys("mod+3", () => emit("hotkey:tab-index", 2), {
    preventDefault: true,
  });
  useHotkeys("mod+4", () => emit("hotkey:tab-index", 3), {
    preventDefault: true,
  });
  useHotkeys("mod+5", () => emit("hotkey:tab-index", 4), {
    preventDefault: true,
  });
  useHotkeys("mod+6", () => emit("hotkey:tab-index", 5), {
    preventDefault: true,
  });
  useHotkeys("mod+7", () => emit("hotkey:tab-index", 6), {
    preventDefault: true,
  });
  useHotkeys("mod+8", () => emit("hotkey:tab-index", 7), {
    preventDefault: true,
  });
  useHotkeys("mod+9", () => emit("hotkey:tab-index", 8), {
    preventDefault: true,
  });
  useHotkeys("mod+0", () => emit("hotkey:tab-index", 9), {
    preventDefault: true,
  });

  // Ask AI modal
  useHotkeys("mod+k", () => emit("ai-assistant:open"), {
    preventDefault: true,
  });

  // My Strategies panel
  useHotkeys("mod+shift+enter", () => emit("strategies:open"), {
    preventDefault: true,
  });

  // New tab / close tab / duplicate tab
  useHotkeys("mod+enter", () => emit("hotkey:tab-new"), {
    preventDefault: true,
  });
  useHotkeys("mod+backspace", () => emit("hotkey:tab-close"), {
    preventDefault: true,
  });
  useHotkeys("mod+d", () => emit("hotkey:tab-duplicate"), {
    preventDefault: true,
  });

  // Toggle sim chart (payoff ↔ decay)
  useHotkeys("mod+e", () => emit("hotkey:toggle-sim"), {
    preventDefault: true,
  });

  // Shortcuts help (? key = shift+/) — bound in ShortcutsModal via raw keydown
  // react-hotkeys-hook doesn't reliably match "?" as a hotkey string

  // Enter / Escape — only fire when not focused on form elements (default behavior)
  useHotkeys("enter", () => emit("hotkey:enter"));
}
